// Admin Applications tab — inbox of quick-apply submissions from job listings.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import api from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return d; }
}

export default function AdminApplications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/applications");
      setRows(r.data || []);
    } catch { toast.error("Failed to load applications"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const downloadCv = async (appId) => {
    try {
      const token = localStorage.getItem("dhp_admin_token");
      const r = await fetch(`${API_BASE}/api/admin/applications/${appId}/cv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("CV not available");
      const b = await r.blob();
      const url = URL.createObjectURL(b);
      window.open(url, "_blank", "noopener");
    } catch { toast.error("CV not available for this application"); }
  };

  return (
    <div data-testid="admin-applications-tab">
      <div className="mb-6">
        <h2 className="text-xl font-serif text-navy">Applications Inbox</h2>
        <p className="text-sm text-navy/55 mt-1">{rows.length} total · from job listings</p>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin text-navy/40 mx-auto" /></div>
      ) : (
        <div className="rounded-lg border border-navy/10 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-navy/[0.02] border-b border-navy/10">
              <tr>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Received</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Name</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Email / Phone</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Target role</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">State</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Priority</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">Source</th>
                <th className="text-left px-5 py-3 font-semibold text-navy/70">CV</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="8" className="text-center py-8 text-navy/50">No applications yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-navy/5 hover:bg-navy/[0.02]" data-testid={`application-row-${r.id}`}>
                  <td className="px-5 py-3 text-navy/60 whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="px-5 py-3 font-medium text-navy">{r.full_name}</td>
                  <td className="px-5 py-3 text-navy/70">
                    <div>{r.email}</div>
                    {r.phone && <div className="text-xs text-navy/50 mt-0.5">{r.phone}</div>}
                  </td>
                  <td className="px-5 py-3 text-navy/70">{r.target_role}</td>
                  <td className="px-5 py-3 text-navy/70 whitespace-nowrap">{r.state_label || r.state || "—"}</td>
                  <td className="px-5 py-3">
                    {r.priority_tier === "high" ? (
                      <span className="rounded-full bg-[#D4AF37] text-[#0F2A1F] text-[10px] font-bold px-2 py-0.5">★ TOP</span>
                    ) : r.priority_tier === "priority" ? (
                      <span className="rounded-full bg-[#10B981]/15 text-[#10B981] text-[10px] font-semibold px-2 py-0.5">Priority</span>
                    ) : (
                      <span className="text-xs text-navy/40">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-navy/60">{r.utm_source || "—"}</td>
                  <td className="px-5 py-3">
                    {r.cv_filename ? (
                      <button onClick={() => downloadCv(r.id)} className="text-[#10B981] hover:underline text-xs font-medium inline-flex items-center gap-1" data-testid={`download-cv-${r.id}`}>
                        <Download className="h-3.5 w-3.5" /> View CV
                      </button>
                    ) : <span className="text-navy/40 text-xs">No CV</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
